import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { AnyObject } from 'yup';
import moment from 'moment';
import { CLIENT_AUTHORIZATION_MESSAGE } from '../../constants/authorizationMessages';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Theme, Typography } from '@mui/material';

import { ReactComponent as LogoIcon } from '../LandingView/assets/logo.svg';

import {
  confirmClientEmail,
  getClientDataByEmailConfirmationToken,
  getPlaintiffConsentDocument
} from '../../api/clientsApi/clientsApi';
import Loader from '../../components/Loader';
import FormDateField from '../../components/formFields/FormDateField';
import FormMaskedTextField from '../../components/formFields/FormMaskedTextField';
import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import ConsentAuthorizationSection from '../../components/ConsentAuthorizationSection';
import { emailConfirmationFormSchema } from '../../helpers/formSchemas/formSchemas';
import { EMAIL_CONFIRMATION_FORM_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import useSnackBar from '../../hooks/useSnackBar';
import { ErrorData } from '../../types';

const useStyles = makeStyles()((theme: Theme) => ({
  pageBackground: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(to left bottom, rgba(37, 157, 168, 0.19), rgba(37, 157, 168, 0))',
    backgroundColor: '#282B3A',
    padding: '20px 20px',

    '@media (max-width: 640px)': {
      padding: '20px',
    },
  },
  signInLogoContainer: {
    marginBottom: '30px',
    svg: {
      color: theme.palette.common.white,
    },
  },
  formContentText: {
    fontSize: '16px',
    lineHeight: '120%',
    fontFamily: 'Inter',
    fontWeight: 400,
    fontStyle: 'normal',
    color: theme.palette.common.white,
    textAlign: 'center',
  },
  formContainer: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
    padding: '32px',
    maxWidth: '480px',
    boxSizing: 'border-box',
    borderRadius: '16px',
    width: '100%',
  },
  formTitle: {
    fontSize: '34px',
    lineHeight: '120%',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontStyle: 'normal',
    padding: 0,
    marginBottom: '40px',
  },
  supportLink: {
    fontFamily: 'Inter',
    fontSize: '16px',
    lineHeight: '120%',
    fontWeight: 400,
    fontStyle: 'normal',
    color: theme.palette.info.main,
    textDecoration: 'none',
  },
  welcomeText: {
    fontSize: '34px',
    lineHeight: '120%',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontStyle: 'normal',
    color: theme.palette.common.white,
    marginBottom: '20px',
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    margin: '0px',
  },
}));

interface ConfirmClientEmailFormProps {
  ssn: string;
  birthdate: string | Date | AnyObject | null;
}

const EmailConfirmView = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();

  const [isClientDataLoading, setIsClientDataLoading] = useState<boolean>(true);
  const [isConfirmEmailFormSubmiting, setIsConfirmEmailFormSubmiting] = useState<boolean>(false);
  const [isConsentAgreed, setIsConsentAgreed] = useState<boolean>(false);
  const [hasConfirmedBefore, setHasConfirmedBefore] = useState<boolean>(false);
  const [consentDocPath, setConsentDocPath] = useState<string | null>(null);
  const [clientData, setClientData] = useState<{
    token: 'string';
    isTokenExpired: true;
    clientFirstName: 'string';
  }>();

  const params = new URLSearchParams(location.search);
  const token = params.get('token');

  const handleViewTermsAndConditions = async () => {
    if (!consentDocPath) {
      setAlert((prev) => ({
        ...prev,
        message: 'Terms & Conditions document not found.',
        type: 'error',
      }));
      return;
    }

  if (!token) {
      setAlert((prev) => ({
        ...prev,
        message: 'Invalid or missing token.',
        type: 'error',
      }));
      return;
    }
    try {
      const encodedDocumentKey = encodeURIComponent(consentDocPath);
      const response = await getPlaintiffConsentDocument(token, encodedDocumentKey);
      if (response.data?.downloadUrl) {
        const fileUrl = response.data.downloadUrl;
        // Open document in new tab for viewing
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = '';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setAlert((prev) => ({
          ...prev,
          message: response.data?.message || 'No file found to view.',
          type: 'info',
        }));
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'Error viewing Terms & Conditions',
        type: 'error',
      }));
    }
  };

  // Authorization message for consent section
  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(emailConfirmationFormSchema),
    defaultValues: EMAIL_CONFIRMATION_FORM_DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = formMethods;

  const onSubmit = (values: ConfirmClientEmailFormProps) => {
    setIsConfirmEmailFormSubmiting(true);

    const submitData = {
      ssn: values.ssn,
      birthdate: moment(values.birthdate).format('YYYY-MM-DD'),
      confirmationToken: token,
    };

    confirmClientEmail(submitData)
      .then((res) => {
        if (res.status === 204) {
          setAlert((prev) => ({
            ...prev,
            type: 'success',
            message: 'Email has been successfully confirmed.',
          }));

          navigate('/');
        }
      })
      .catch((err) => {
        setIsConfirmEmailFormSubmiting(false);
        setAlert((prev) => ({
          ...prev,
          type: 'error',
          message: err.response.data.message,
        }));
      });
  };

  useEffect(() => {
    if (token?.length) {
      getClientDataByEmailConfirmationToken(token as string)
        .then((res) => {
          if (!res.data.isTokenExpired) {
            setClientData(res.data);
            setIsClientDataLoading(false);

            // Handle consentRequired field from API
            if (res.data.consentRequired) {
              setHasConfirmedBefore(false);
            } else {
              setHasConfirmedBefore(true);
            }

            // Handle consentDocPath field from API
            if (res.data.consentDocPath) {
              setConsentDocPath(res.data.consentDocPath);
            }
          }
        })
        .catch((err: AxiosError<ErrorData>) => {
          const errorMessage = err.response?.data.message || 'Error. Something went wrong...';

          setAlert((prev) => ({
            ...prev,
            message: errorMessage,
            type: 'error',
          }));
          navigate('/');
        });
    } else {
      navigate('/');
    }
  }, [token]);

  if (isClientDataLoading) {
    return (
      <Box className={classes.pageBackground}>
        <Box mb="100px">
          <Loader colorType="warning" />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.pageBackground}>
      <Box className={classes.signInLogoContainer}>
        <Link to="/">
          <LogoIcon />
        </Link>
      </Box>

      {clientData && (
        <>
          <Typography
            className={classes.welcomeText}
          >{`Hello, ${clientData.clientFirstName}`}</Typography>
          <Box className={classes.formContainer}>
            <FormProvider {...formMethods}>
              <Typography className={classes.formContentText}>
                To finish the confirmation,
              </Typography>
              <Typography className={classes.formContentText} mb="0px">
                please enter your SSN and Date of Birth.
              </Typography>

              <Box mt="32px">
                <FormMaskedTextField
                  mask="999-99-9999"
                  name="ssn"
                  label="Social Security Number *"
                />
              </Box>

              <Box mt="24px" mb="40px">
                <FormDateField name="birthdate" label="Date of Birth *" maxDate={moment()} />
              </Box>

              {/* Consent & Authorization Section */}
              {!hasConfirmedBefore && (
                <Box className={classes.divider} sx={{ marginBottom: '8px' }} />
              )}
              {!hasConfirmedBefore && (
                <Box mt="30px">
                  <ConsentAuthorizationSection
                    authorizationMessage={CLIENT_AUTHORIZATION_MESSAGE}
                    isAgreed={isConsentAgreed}
                    onAgreementChange={setIsConsentAgreed}
                    onTermsClick={handleViewTermsAndConditions}
                  />
                </Box>
              )}

              <Box display="flex" mt="32px">
                <FormSubmitButton
                  onClick={handleSubmit(onSubmit)}
                  buttonText="Submit"
                  fontFamily="Inter"
                  disabled={!isValid || isConfirmEmailFormSubmiting || (!hasConfirmedBefore && !isConsentAgreed)}
                />
              </Box>
            </FormProvider>
          </Box>
        </>
      )}
    </Box>
  );
};

export default EmailConfirmView;